import { create } from 'zustand';

interface CartModifier {
  modifierGroupId: string;
  modifierOptionId: string;
  name: string;
  priceAdjustment: number;
}

interface CartItem {
  id: string;
  menuItemId: string;
  categoryId?: string;
  name: string;
  quantity: number;
  size: 'small' | 'medium' | 'large';
  sweetness: '0' | '25' | '50' | '70' | '100';
  modifiers: CartModifier[];
  unitPrice: number;
  totalPrice: number;
  notes?: string;
}

interface AppliedPromotion {
  promotionId: string;
  name: { en: string; th: string };
  type: string;
  discountAmount: number;
}

interface CartState {
  items: CartItem[];
  customerId?: string;
  loyaltyPointsToRedeem: number;
  appliedPromotion?: AppliedPromotion;

  addItem: (item: Omit<CartItem, 'id' | 'totalPrice'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  setCustomer: (customerId: string | undefined) => void;
  setLoyaltyPointsToRedeem: (points: number) => void;
  setAppliedPromotion: (promotion: AppliedPromotion | undefined) => void;
  clearCart: () => void;

  // Computed values
  getSubtotal: () => number;
  getTax: () => number;
  getDiscount: () => number;
  getPromotionDiscount: () => number;
  getLoyaltyDiscount: () => number;
  getTotal: () => number;
}

export type { CartItem, AppliedPromotion };

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  customerId: undefined,
  loyaltyPointsToRedeem: 0,
  appliedPromotion: undefined,

  addItem: (item) => {
    const id = `${item.menuItemId}-${item.size}-${item.sweetness}-${Date.now()}`;
    const modifierTotal = item.modifiers.reduce((sum, m) => sum + m.priceAdjustment, 0);
    const totalPrice = (item.unitPrice + modifierTotal) * item.quantity;

    set((state) => ({
      items: [
        ...state.items,
        {
          ...item,
          id,
          totalPrice,
        },
      ],
      // Clear promotion when cart changes (will be recalculated)
      appliedPromotion: undefined,
    }));
  },

  removeItem: (id) => {
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
      // Clear promotion when cart changes
      appliedPromotion: undefined,
    }));
  },

  updateQuantity: (id, quantity) => {
    if (quantity <= 0) {
      get().removeItem(id);
      return;
    }

    set((state) => ({
      items: state.items.map((item) =>
        item.id === id
          ? { ...item, quantity, totalPrice: item.unitPrice * quantity }
          : item
      ),
      // Clear promotion when cart changes
      appliedPromotion: undefined,
    }));
  },

  setCustomer: (customerId) => set({ customerId }),

  setLoyaltyPointsToRedeem: (loyaltyPointsToRedeem) => set({ loyaltyPointsToRedeem }),

  setAppliedPromotion: (appliedPromotion) => set({ appliedPromotion }),

  clearCart: () => set({ items: [], customerId: undefined, loyaltyPointsToRedeem: 0, appliedPromotion: undefined }),

  getSubtotal: () => {
    const { items } = get();
    return items.reduce((sum, item) => sum + item.totalPrice, 0);
  },

  getTax: () => {
    // Tax is calculated on subtotal after promotion discount, before loyalty points
    const subtotal = get().getSubtotal();
    const promoDiscount = get().getPromotionDiscount();
    return (subtotal - promoDiscount) * 0.07; // 7% VAT
  },

  getPromotionDiscount: () => {
    return get().appliedPromotion?.discountAmount || 0;
  },

  getLoyaltyDiscount: () => {
    return get().loyaltyPointsToRedeem; // 1 point = 1 THB
  },

  getDiscount: () => {
    return get().getPromotionDiscount() + get().getLoyaltyDiscount();
  },

  getTotal: () => {
    const subtotal = get().getSubtotal();
    const promoDiscount = get().getPromotionDiscount();
    const tax = get().getTax();
    const loyaltyDiscount = get().getLoyaltyDiscount();
    return subtotal - promoDiscount + tax - loyaltyDiscount;
  },
}));
